import React, { FC, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Button } from "@natera/form";
import { Svg } from "@natera/material/lib/svg";
import { routes } from "@app/routing";
import { HEAP_EVENTS } from "@app/provider/types";
import { usePatientEducation } from "@app/provider";
import { usePatientEducationHeap } from "@app/hooks/usePatientEducationHeap";
import { PatientEducationLoader } from "./PatientEducationLoader";

import ViewVideoIcon from "@assets/svg/view-video.svg";
import InfoIcon from "@assets/svg/icons/info.svg";
import TestTubeIcon from "@assets/svg/icons/test-tube.svg";

import "./patientEducation.scss";

const messages = defineMessages({
  title: {
    id: "patientEducation.landing.title",
    defaultMessage: "A Healthy Start Begins Here",
  },
  description: {
    id: "patientEducation.landing.description",
    defaultMessage:
      "{clinicName} would like you to learn about the two types of genetic tests that will be offered at your upcoming appointment.",
  },
  instruction: {
    id: "patientEducation.landing.instruction",
    defaultMessage:
      "Prior to your appointment, please watch two educational videos on Non-Invasive Prenatal Testing and Carrier Screening, then complete a quick questionnaire that will be shared with your provider.",
  },
  infoMessage: {
    id: "patientEducation.landing.infoMessage",
    defaultMessage:
      "If you have additional questions following the questionnaire, you can schedule a complimentary genetic information session to speak 1:1 with a genetic counselor.",
  },
  panoramaTitle: {
    id: "patientEducation.landing.panoramaTitle",
    defaultMessage: "Panorama Non-Invasive Prenatal Screening Test",
  },
  panoramaDescription: {
    id: "patientEducation.landing.panoramaDescription",
    defaultMessage:
      "This test looks for certain chromosome differences with the baby.",
  },
  horizonTitle: {
    id: "patientEducation.landing.horizonTitle",
    defaultMessage: "Horizon Carrier Screening",
  },
  horizonDescription: {
    id: "patientEducation.landing.horizonDescription",
    defaultMessage:
      "This screening looks at your genetic information to determine your chance of passing a genetic condition to the baby.",
  },
  beginButton: {
    id: "patientEducation.landing.beginButton",
    defaultMessage: "Begin",
  },
});

const LandingPage: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { publicClinicId, clinic } = usePatientEducation();
  const { trackEvent } = usePatientEducationHeap(publicClinicId);

  // Track landing page view
  useEffect(() => {
    if (clinic && publicClinicId) {
      trackEvent({ eventKey: HEAP_EVENTS.upp_prenataled_landing });
    }
  }, [clinic, publicClinicId, trackEvent]);

  const handleBegin = () => {
    if (publicClinicId) {
      trackEvent({ eventKey: HEAP_EVENTS.upp_prenataled_begin });
      navigate(routes.patientEducationPanoramaPage(publicClinicId));
    }
  };

  return (
    <PatientEducationLoader>
      <div className="patient-education">
        <div className="patient-education__container">
          <h1 className="patient-education__title">
            {intl.formatMessage(messages.title)}
          </h1>

          <p className="patient-education__description">
            {intl.formatMessage(messages.description, {
              clinicName: clinic?.name,
            })}
          </p>

          <div className="patient-education__icon-large">
            <Svg content={ViewVideoIcon} />
          </div>

          <p className="patient-education__instruction">
            {intl.formatMessage(messages.instruction)}
          </p>

          <div className="patient-education__info-banner">
            <Svg content={InfoIcon} className="patient-education__info-icon" />
            <p>{intl.formatMessage(messages.infoMessage)}</p>
          </div>

          <div className="patient-education__test-cards">
            <div className="patient-education__test-card">
              <div className="patient-education__test-icon">
                <Svg content={TestTubeIcon} />
              </div>
              <div>
                <h3 className="patient-education__test-title">
                  {intl.formatMessage(messages.panoramaTitle)}
                </h3>
                <p className="patient-education__test-description">
                  {intl.formatMessage(messages.panoramaDescription)}
                </p>
              </div>
            </div>

            <div className="patient-education__test-card">
              <div className="patient-education__test-icon">
                <Svg content={TestTubeIcon} />
              </div>
              <div>
                <h3 className="patient-education__test-title">
                  {intl.formatMessage(messages.horizonTitle)}
                </h3>
                <p className="patient-education__test-description">
                  {intl.formatMessage(messages.horizonDescription)}
                </p>
              </div>
            </div>
          </div>

          <div className="patient-education__actions">
            <Button
              onClick={handleBegin}
              disabled={!clinic}
              className="patient-education__button"
              unelevated
            >
              {intl.formatMessage(messages.beginButton)}
            </Button>
          </div>
        </div>
      </div>
    </PatientEducationLoader>
  );
};

export default LandingPage;
